import { alpha, type SxProps, type Theme } from "@mui/material/styles";

import { customGradients } from "@/theme/customTokens";

export const root: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2.25),
});

export const profileCard: SxProps<Theme> = (theme) => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: 3,
  border: 1,
  borderColor: "divider",
  bgcolor: "background.paper",
  pt: { xs: 5.5, md: 6.5 },
  px: { xs: 2, md: 2.75 },
  pb: { xs: 2, md: 2.5 },
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  alignItems: { xs: "flex-start", sm: "flex-end" },
  gap: theme.spacing(1.75),
  "&::before": {
    content: '""',
    position: "absolute",
    insetInline: 0,
    top: 0,
    height: { xs: 64, md: 76 },
    background: customGradients.instagram,
    opacity: 0.9,
  },
});

export const avatarSlot: SxProps<Theme> = {
  position: "relative",
};

export const identity: SxProps<Theme> = (theme) => ({
  position: "relative",
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
});

export const displayName: SxProps<Theme> = {
  fontSize: { xs: "1rem", md: "1.125rem" },
  fontWeight: 800,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export const metaRow: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.spacing(1),
});

export const username: SxProps<Theme> = (theme) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  fontSize: "0.8125rem",
  fontWeight: 600,
  color: "text.secondary",
  textDecoration: "none",
  "&:hover": { color: "primary.main" },
});

export const typeChip: SxProps<Theme> = {
  fontSize: "0.6875rem",
  fontWeight: 600,
  color: "text.secondary",
  bgcolor: "background.default",
  borderRadius: 1.25,
  px: 1,
  py: 0.25,
};

export const attention: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.25),
  p: theme.spacing(1.75),
  borderRadius: 2,
  bgcolor: alpha(theme.palette.warning.main, 0.1),
  border: 1,
  borderColor: alpha(theme.palette.warning.main, 0.35),
});

export const attentionTitle: SxProps<Theme> = {
  fontSize: "0.8125rem",
  fontWeight: 700,
};

export const attentionText: SxProps<Theme> = {
  fontSize: "0.75rem",
  color: "text.secondary",
  lineHeight: 1.8,
};

export const tiles: SxProps<Theme> = (theme) => ({
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
  gap: theme.spacing(1.25),
});

export const scopes: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
});

export const sectionLabel: SxProps<Theme> = {
  fontSize: "0.75rem",
  fontWeight: 700,
};

export const scopeList: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(0.75),
});

export const scopeChip: SxProps<Theme> = (theme) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  px: 1.25,
  py: 0.5,
  borderRadius: 1.25,
  fontSize: "0.71875rem",
  fontWeight: 600,
  bgcolor: alpha(theme.palette.primary.main, 0.08),
  color: "primary.main",
});

export const lastError: SxProps<Theme> = {
  fontSize: "0.6875rem",
  color: "error.main",
  textAlign: "start",
  wordBreak: "break-word",
};

export const footer: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: { xs: "column-reverse", sm: "row" },
  justifyContent: "space-between",
  gap: theme.spacing(1.25),
  pt: 2.25,
  borderTop: 1,
  borderColor: "divider",
});

export const footerButton: SxProps<Theme> = {
  width: { xs: "100%", sm: "auto" },
};

export const smallAction: SxProps<Theme> = {
  minHeight: 32,
  fontSize: "0.75rem",
};
